import { useEffect, useState, useSyncExternalStore } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Pill } from "../lib/primitives";
import { SaveBar } from "../components/SaveBar";
import { CallbackOriginsCard } from "../components/CallbackOrigins";
import { readableTextOn } from "../lib/contrast";
import { apiClient, ApiError, type ProviderBranding } from "../lib/api";
import { useOperatorRole } from "../lib/hooks";

// Hosted pages follow each visitor's `prefers-color-scheme` by default
// (`auto`); light/dark are opt-in overrides that force one theme.
const MODES: Array<[ProviderBranding["mode"], string]> = [
  ["auto", "Match device"],
  ["light", "Always light"],
  ["dark", "Always dark"],
];

const DARK_QUERY = "(prefers-color-scheme: dark)";

/** The operator's own OS preference — stands in for the visitor's in the
 *  preview when the mode is `auto`. */
function usePrefersDark(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mql = window.matchMedia(DARK_QUERY);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    () => window.matchMedia(DARK_QUERY).matches,
  );
}

/**
 * Sign-in page: how the hosted pages look for this app (name, logo, color,
 * theme) and where they send people back to. Merges the old Brand identity
 * and Hosted pages screens.
 */
export function SigninPage() {
  const queryClient = useQueryClient();
  const { canWrite } = useOperatorRole();
  const prefersDark = usePrefersDark();
  const { data, isLoading } = useQuery({
    queryKey: ["branding"],
    queryFn: () => apiClient.branding.get(),
    staleTime: 60_000,
  });

  const [draft, setDraft] = useState<ProviderBranding | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data && !draft) setDraft(data);
  }, [data, draft]);

  const updateMutation = useMutation({
    mutationFn: (patch: Partial<ProviderBranding>) => apiClient.branding.update(patch),
    onSuccess: (saved) => {
      setError(null);
      setDraft(saved);
      queryClient.setQueryData(["branding"], saved);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed to save brand identity");
    },
  });

  if (isLoading || !data || !draft) {
    return <div style={{ padding: 24, fontSize: 13, color: "var(--ad-fg-3)" }}>Loading…</div>;
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(data);
  // Viewers get a read-only screen (#52): every edit funnels through these
  // setters, so the guard freezes the draft; the publish footer is hidden
  // below. The server still enforces RBAC on PATCH.
  const set = <K extends keyof ProviderBranding>(k: K, v: ProviderBranding[K]) =>
    canWrite && setDraft((d) => (d ? { ...d, [k]: v } : d));
  // One brand color drives both the primary action and the accent — the
  // co-brand surface contributes a single color, not a palette.
  const setColor = (v: string) =>
    canWrite && setDraft((d) => (d ? { ...d, primary: v, accent: v } : d));

  const onDiscard = () => {
    setDraft(data);
    setError(null);
  };
  const onPublish = () => {
    if (!dirty) return;
    updateMutation.mutate(draft);
  };

  return (
    <>
      <div className="ad-page-head">
        <div className="ad-page-head-l">
          <div className="ad-eyebrow">Set up</div>
          <h1 className="ad-h1">Sign-in page</h1>
          <div className="ad-sub">
            What people see when they sign in to your app with AuthenSee. Changes go live in a
            few seconds.
          </div>
        </div>
        <div className="ad-page-head-r">
          {/* Publish moved to the contextual bottom SaveBar. */}
          {!canWrite && <Pill kind="muted">Read-only</Pill>}
        </div>
      </div>

      <div className="ad-brand-grid">
        <div className="ad-stack-lg">
          <div className="ad-card">
            <div className="ad-card-head"><div className="ad-card-title">Your app</div></div>
            <div className="ad-stack-md">
              <div className="ad-field">
                <div className="ad-field-label">App name</div>
                <input className="ad-input" maxLength={50} placeholder="Acme" value={draft.logoText} onChange={(e) => set("logoText", e.target.value)} />
                <div className="ad-field-hint">Shown at the top of the page (“Sign in to {draft.logoText || "Acme"}”), and in place of a logo if you don’t add one. Up to 50 characters.</div>
              </div>
              <div className="ad-field">
                <div className="ad-field-label">Logo</div>
                <input className="ad-input ad-input-mono" placeholder="https://cdn.acme.com/logo.svg" value={draft.logoUrl ?? ""} onChange={(e) => set("logoUrl", e.target.value || null)} />
                <div className="ad-field-hint">Optional. A link to your logo (https://) or a data: URI.</div>
              </div>
              <div className="ad-field">
                <div className="ad-field-label">Brand color</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="color" value={draft.primary} onChange={(e) => setColor(e.target.value)} style={{ width: 44, height: 32, padding: 0, border: 0, borderRadius: 8, background: "transparent" }} />
                  <input className="ad-input ad-input-mono" value={draft.primary} onChange={(e) => setColor(e.target.value)} />
                </div>
                <div className="ad-field-hint">Used for the main button. We pick black or white text so it stays readable.</div>
              </div>
              <div className="ad-field">
                <div className="ad-field-label">Theme</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {MODES.map(([v, l]) => (
                    <button key={v} className="ad-filter-chip" data-active={draft.mode === v} onClick={() => set("mode", v)} style={{ whiteSpace: "nowrap" }}>{l}</button>
                  ))}
                </div>
                <div className="ad-field-hint">Sign-in pages follow each person's light or dark setting unless you choose one.</div>
              </div>
            </div>
          </div>

          <CallbackOriginsCard />

          {error && (
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                fontSize: 13,
                background: "color-mix(in oklab, var(--ad-error) 10%, transparent)",
                color: "var(--ad-error)",
              }}
            >
              {error}
            </div>
          )}
        </div>

        <div id="brand-preview" className="ad-brand-preview-anchor">
          <div className="ad-eyebrow-sm" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
            Preview
            {draft.mode === "auto" && <Pill kind="muted">{prefersDark ? "Dark" : "Light"}, like your device</Pill>}
          </div>
          <SigninPreview brand={draft} isDark={draft.mode === "auto" ? prefersDark : draft.mode === "dark"} />
        </div>
      </div>

      {/* Mobile-only: the preview stacks below the form, so surface a jump to it. */}
      <a href="#brand-preview" className="ad-brand-preview-jump">See preview ↓</a>

      <SaveBar
        visible={canWrite && dirty}
        pending={updateMutation.isPending}
        onDiscard={onDiscard}
        onPublish={onPublish}
        label="Publish"
        pendingLabel="Publishing…"
      />
    </>
  );
}

/**
 * The hosted "Confirm it's you" screen (authnc-hosted-pages `Screen`): the
 * app's mark and the step on top, one picture, a title and one sentence, the
 * main button in the brand color, "Secured by AuthenSee" below. Light/dark
 * flips the surface; for `auto` the caller resolves it from this device.
 */
function SigninPreview({ brand, isDark }: { brand: ProviderBranding; isDark: boolean }) {
  const name = brand.logoText.trim() || "Acme";
  const t = isDark
    ? { bg: "#000000", fg1: "#FFFFFF", fg2: "#CFCFCF", fg3: "#9C9C9C", line: "rgba(255,255,255,0.12)", pic: "rgba(255,123,26,0.14)", ink: "#FF8F3D" }
    : { bg: "#FFFFFF", fg1: "#0A0A0A", fg2: "#45413D", fg3: "#69645F", line: "rgba(10,10,10,0.10)", pic: "#FFF1E5", ink: "#B04F06" };
  return (
    <div
      style={{
        maxWidth: 440,
        borderRadius: 24,
        overflow: "hidden",
        border: `1px solid ${isDark ? "#2A2A2A" : "var(--ad-border-1)"}`,
        background: t.bg,
        color: t.fg1,
        fontFamily: "var(--rb-font-sans)",
        display: "flex",
        flexDirection: "column",
        minHeight: 560,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: `1px solid ${t.line}` }}>
        {brand.logoUrl ? (
          <img src={brand.logoUrl} alt={name} style={{ height: 28, maxHeight: 28, width: "auto", objectFit: "contain" }} />
        ) : (
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 30, height: 30, borderRadius: 8, background: brand.primary, color: readableTextOn(brand.primary), display: "grid", placeItems: "center", fontSize: 14, fontWeight: 700 }}>
              {name.charAt(0).toUpperCase()}
            </span>
            <span style={{ fontSize: 16, fontWeight: 600 }}>{name}</span>
          </span>
        )}
        <span style={{ fontSize: 14, color: t.fg3 }}>Sign in</span>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 20, padding: "24px 24px 8px" }}>
        <div style={{ height: 160, borderRadius: 20, background: t.pic, color: t.ink, display: "grid", placeItems: "center" }}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="8" cy="15" r="4" />
            <path d="M10.85 12.15 19 4M18 5l2 2M15 8l2 2" />
          </svg>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 26, fontWeight: 600, lineHeight: 1.12, letterSpacing: "-0.025em" }}>Confirm it’s you</div>
          <div style={{ fontSize: 15, lineHeight: 1.55, color: t.fg2 }}>
            Use Face ID, your fingerprint or your security key to sign in to {name}.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          tabIndex={-1}
          style={{ padding: "13px 18px", background: brand.primary, color: readableTextOn(brand.primary), border: 0, borderRadius: 999, fontWeight: 600, fontSize: 15, cursor: "default", marginBottom: 16 }}
        >
          Continue with passkey
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 24px", borderTop: `1px solid ${t.line}`, fontSize: 13, color: t.fg3 }}>
        <span>
          Secured by{" "}
          <span style={{ fontWeight: 600, color: t.fg1 }}>
            Authen<span style={{ fontFamily: "var(--rb-font-serif)", fontStyle: "italic", fontWeight: 400, color: t.ink }}>See</span>
          </span>
        </span>
        <span style={{ color: t.fg2 }}>Help</span>
      </div>
    </div>
  );
}
